import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {buildPanelChangeSummary} from '../helpers/panelChangeSummary';
import {getPanelChangeTitleLines, resolvePanelChangeActors} from '../helpers/panelChangeLog';
import {usePanelChangeListStyles} from '../helpers/panelChangeListStyles';
import PanelChangeRow from './PanelChangeRow';
import PanelChangeScopeBadge from './PanelChangeScopeBadge';
import PanelChangeAdUnitList from './PanelChangeAdUnitList';
import PanelChangeTitle from './PanelChangeTitle';

const buildScopeKey = (bulletId, sectionId) => `${bulletId}|${sectionId}`;

const PanelChangeSummary = ({
  changes,
  fill = false,
  onSelectAdUnit,
  hideScopeBadges = false,
  attributeGroups = null,
  actorView = null,
}) => {
  const classes = usePanelChangeListStyles();
  const {bullets} = React.useMemo(() => buildPanelChangeSummary(changes), [changes]);
  const [expandedScopeKey, setExpandedScopeKey] = useState(null);

  useEffect(() => {
    setExpandedScopeKey(null);
  }, [changes]);

  const toggleScope = key => {
    setExpandedScopeKey(current => (current === key ? null : key));
  };

  if (bullets.length === 0) {
    return (
      <Box className={`${classes.list} ${fill ? classes.listFill : ''}`}>
        <Typography variant="body2" color="textSecondary" className={classes.empty}>
          No changes to summarize.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={`${classes.list} ${fill ? classes.listFill : ''}`}>
      {bullets.map(bullet => {
        const valueTransitions = bullet.transitions.filter(
          transition => transition.showValues && !transition.isToggle
        );
        const hasMultipleValueTransitions = valueTransitions.length > 1;
        const showHeaderScope = !hideScopeBadges && (bullet.isEnabledToggle || !hasMultipleValueTransitions);
        const showTransitionScopes = !hideScopeBadges && hasMultipleValueTransitions;
        const headerScopeKey = buildScopeKey(bullet.id, 'header');
        const headerExpanded = expandedScopeKey === headerScopeKey;
        const canExpandHeader = Boolean(onSelectAdUnit && bullet.adUnits?.length);
        const title = getPanelChangeTitleLines(bullet, attributeGroups);

        return (
          <Box key={bullet.id} className={classes.summaryGroup}>
            <Box className={classes.summaryGroupHeader}>
              <Box className={classes.summaryGroupTitle}>
                <PanelChangeTitle
                  classes={classes}
                  primaryLine={title.primaryLine}
                  secondaryLine={title.secondaryLine}
                  sspLogo={title.sspLogo}
                  isTagLevel={bullet.isTagLevel}
                />
              </Box>
              {showHeaderScope ? (
                <PanelChangeScopeBadge
                  classes={classes}
                  scope={bullet.scope}
                  interactive={canExpandHeader}
                  expanded={headerExpanded}
                  onToggle={() => toggleScope(headerScopeKey)}
                />
              ) : null}
            </Box>

            {showHeaderScope && headerExpanded ? (
              <PanelChangeAdUnitList
                classes={classes}
                adUnits={bullet.adUnits}
                isTagLevel={bullet.isTagLevel}
                onSelectAdUnit={onSelectAdUnit}
              />
            ) : null}

            {bullet.transitions.map(transition => {
              const transitionScopeKey = buildScopeKey(bullet.id, transition.id);
              const transitionExpanded = expandedScopeKey === transitionScopeKey;
              const canExpandTransition = Boolean(onSelectAdUnit && transition.adUnits?.length);

              return (
                <React.Fragment key={transition.id}>
                  <Box className={classes.summaryTransitionRow}>
                    <Box className={classes.summaryTransitionValues}>
                      <PanelChangeRow
                        classes={classes}
                        attName=""
                        oldValue={transition.oldValue}
                        newValue={transition.newValue}
                        showValues={transition.showValues}
                        valueTone={transition.valueTone}
                        singleValue={transition.isToggle}
                        hideLabels
                        actors={resolvePanelChangeActors(
                          transition.actors,
                          actorView?.auth,
                          actorView?.show ?? true
                        )}
                        rowClassName={classes.summaryGroupRow}
                      />
                    </Box>
                    {showTransitionScopes && transition.scope ? (
                      <PanelChangeScopeBadge
                        classes={classes}
                        scope={transition.scope}
                        interactive={canExpandTransition}
                        expanded={transitionExpanded}
                        onToggle={() => toggleScope(transitionScopeKey)}
                      />
                    ) : null}
                  </Box>

                  {showTransitionScopes && transitionExpanded ? (
                    <PanelChangeAdUnitList
                      classes={classes}
                      adUnits={transition.adUnits}
                      isTagLevel={bullet.isTagLevel}
                      onSelectAdUnit={onSelectAdUnit}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default React.memo(PanelChangeSummary);
